import { IBCS } from './charts/HatchPattern';
import { formatEur } from './charts/IbcsTooltip';
import type { CounterpartyData } from '../../types/cashCockpit';

interface Props {
    data: CounterpartyData;
    concentrationLimitPct?: number;
}

export default function CounterpartyTab({ data, concentrationLimitPct = 30 }: Props) {
    const top = data.concentrations.slice(0, 10);

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Counterparty Concentration (Top 10)</h3>
                <div className="text-xs text-gray-500">
                    Group total: <span className="font-semibold">{formatEur(data.grandTotalEUR)}</span>
                </div>
            </div>
            <div className="space-y-1.5">
                {top.map((c) => {
                    const over = c.pctOfTotal > concentrationLimitPct;
                    return (
                        <div key={c.groupKey} className="flex items-center gap-2">
                            <div className="w-56 text-sm truncate" title={c.groupKey}>{c.groupKey}</div>
                            <div className="flex-1 relative h-5 bg-gray-100">
                                <div
                                    className="absolute top-0 bottom-0 left-0"
                                    style={{
                                        width: `${Math.min(Math.max(c.pctOfTotal, 1), 100)}%`,
                                        backgroundColor: over ? IBCS.negative : IBCS.actual,
                                    }}
                                />
                                {/* Concentration limit line */}
                                <div
                                    className="absolute top-0 bottom-0 border-r border-dashed border-gray-500"
                                    style={{ left: `${concentrationLimitPct}%` }}
                                    title={`Limit ${concentrationLimitPct}%`}
                                />
                            </div>
                            <div className="w-28 text-right text-xs font-mono tabular-nums">{formatEur(c.amountEUR)}</div>
                            <div className={`w-16 text-right text-xs font-mono tabular-nums ${over ? 'text-[#D7191C] font-semibold' : 'text-gray-700'}`}>
                                {c.pctOfTotal.toFixed(1)}%
                            </div>
                            <div className="w-16 text-right text-xs text-gray-500">{c.accountCount} acct</div>
                        </div>
                    );
                })}
            </div>
            <div className="text-xs text-gray-500 mt-3 pt-2 border-t border-gray-100">
                Dashed line marks the internal concentration limit ({concentrationLimitPct}%).
                Groups over the limit are flagged in red. Configure groups in the Settings tab.
            </div>
        </div>
    );
}
